import { captureWhenReady, loadedPostHog, withPostHog } from "./posthog";
import { getReferral } from "./referral";

// ---------------------------------------------------------------------------
// UTM Parameter Capture
// ---------------------------------------------------------------------------

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "li_fat_id", // LinkedIn first-party cookie
  // Google Ads click identifiers, appended by auto-tagging. `gclid` is the
  // normal one; `gbraid` and `wbraid` replace it when iOS App Tracking
  // Transparency blocks gclid, so capturing only gclid silently loses a slice
  // of iOS traffic. `gad_source` records which Google surface the click came
  // from. All four are needed to attribute a lead back to a keyword.
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  // Microsoft Advertising's click identifier, appended by its auto-tagging.
  // The Bing twin of gclid: a paid Bing click carries it, a crawler or a
  // stale persisted UTM does not.
  "msclkid",
] as const;

const UTM_STORAGE_KEY = "rflkt_utm";

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  li_fat_id?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  gad_source?: string;
  msclkid?: string;
  landing_page?: string;
  referrer?: string;
}

/** Capture UTM params from the URL on first landing. Persists to sessionStorage. */
export function captureUtmParams(): void {
  if (typeof window === "undefined") return;

  // Only capture once per session — first touch attribution
  if (sessionStorage.getItem(UTM_STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};
  let hasAny = false;

  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) {
      utm[key] = val;
      hasAny = true;
    }
  }

  // Always store landing page and referrer even without UTMs
  utm.landing_page = window.location.pathname;
  utm.referrer = document.referrer || undefined;

  if (hasAny || document.referrer) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));

    // Set as PostHog person/super properties so they attach to all events.
    // Queued until PostHog loads, ahead of the landing $pageview.
    withPostHog((ph) => ph.register(utm));
  }
}

/** Retrieve stored UTM params (returns empty object if none). */
export function getUtmParams(): UtmParams {
  if (typeof window === "undefined") return {};
  const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/**
 * Everything we know about where this visitor came from: the session's UTMs,
 * click ids, landing page and referrer, plus the partner code (90-day, see
 * referral.ts). Sent with the contact form and the intake so the lead email
 * says who sent it (rflkt/reflektlab#344). The server whitelists the keys.
 */
export function getAttribution(): UtmParams & { ref?: string } {
  const ref = getReferral();
  return { ...getUtmParams(), ...(ref ? { ref } : {}) };
}

// ---------------------------------------------------------------------------
// PostHog Helpers
// ---------------------------------------------------------------------------

/**
 * The visitor's PostHog identity, or undefined when PostHog has not loaded.
 *
 * This is the only value that exists both in the browser at the moment
 * somebody clicks through to meet.reflektlab.ai and in PostHog, so it is the
 * only key that joins a booked meeting back to the session that produced it
 * (RFLKT-7310). The CRM stores it as `bookings.posthog_distinct_id`.
 *
 * UTMs answer which keyword paid for the click. They say nothing about which
 * pages the visitor read before they booked. This is that half.
 *
 * Undefined until PostHog has loaded. There is no PostHog during SSR, and it
 * now starts after the `load` event, so callers that build a link on mount
 * refresh it with withPostHog() (see BookACallButton). Never the literal
 * string "undefined" on a link we hand to prospects.
 */
export function getPostHogDistinctId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return loadedPostHog()?.get_distinct_id() || undefined;
}

/**
 * Track a PostHog event with UTM params automatically attached. Events from
 * before PostHog has loaded are queued, not dropped.
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  if (typeof window === "undefined") return;
  captureWhenReady(event, {
    ...getAttribution(),
    ...properties,
  });
}

/** Track a page view for a solution page. */
export function trackPageView(
  pageName: string,
  properties?: Record<string, unknown>
): void {
  trackEvent(`${pageName}_page_viewed`, {
    page: pageName,
    ...properties,
  });
}

/** Track a CTA click. */
export function trackCTAClick(
  ctaName: string,
  destination: string,
  properties?: Record<string, unknown>
): void {
  trackEvent("cta_clicked", {
    cta_name: ctaName,
    destination,
    ...properties,
  });
}

/**
 * Track a "Book a call" click — the primary conversion signal.
 *
 * The booking completes on Superhuman's calendar with no server-side callback
 * we control, so this outbound click is the furthest-down-funnel event we can
 * observe. It fires as a dedicated `book_call_clicked` event (not the generic
 * `cta_clicked`) so it can back a clean conversion funnel on its own.
 *
 * `placement` records which surface the click came from (navbar, blog,
 * contact, ...) so we can see what drives booking intent. UTM / referrer /
 * landing page are auto-attached by trackEvent, and PostHog's distinct_id ties
 * the click to the person profile (and their email, if ever identified).
 */
export function trackBookCall(
  placement: string,
  properties?: Record<string, unknown>
): void {
  trackEvent("book_call_clicked", {
    // Spread caller props first so the explicit placement always wins.
    ...properties,
    placement,
    source_path:
      typeof window !== "undefined" ? window.location.pathname : undefined,
  });
}

// ---------------------------------------------------------------------------
// Form engagement
// ---------------------------------------------------------------------------

/**
 * Track that a visitor started filling a form — fired on the first focus of a
 * field, before a single character is typed.
 *
 * Deliberately values-free. Session replay masks every input (posthog-js's
 * `maskAllInputs` default) and an abandoned form never reaches /api/contact,
 * so field *names* are the most we can honestly know about someone who chose
 * not to submit — and they answer the question we actually have ("did they
 * engage, or just look at the page?") without collecting personal data nobody
 * agreed to send us.
 */
export function trackFormStarted(form: string, field: string): void {
  trackEvent(`${form}_form_started`, {
    form,
    first_field: field,
  });
}

/**
 * Track a form the visitor engaged with and left without a successful submit.
 *
 * Fires at most once per page visit, on whichever leave signal comes first.
 * Because it is sticky, someone who hides the tab, comes back and *then*
 * submits produces both events — so measure real drop-off as a
 * `contact_form_started` -> `contact_form_submitted` funnel, and read this
 * event for the detail: what they touched, how long they stayed, and whether
 * a submit had already failed on them (`submit_attempts` > 0).
 */
export function trackFormAbandoned(
  form: string,
  properties: {
    fields_touched: string[];
    seconds_engaged: number;
    submit_attempts: number;
    reason: string;
  }
): void {
  trackEvent(`${form}_form_abandoned`, {
    form,
    ...properties,
    fields_touched_count: properties.fields_touched.length,
    // Focus alone starts the form, so this separates "clicked into a field and
    // gave up" from "typed something and gave up".
    typed: properties.fields_touched.length > 0,
  });
}

/** Track contact form submission. */
export function trackFormSubmission(formData: {
  first_name: string;
  last_name: string;
  email_address: string;
  company: string;
}): void {
  const utm = getUtmParams();

  // PostHog event
  trackEvent("contact_form_submitted", {
    company: formData.company,
    has_email: !!formData.email_address,
    has_company: !!formData.company,
  });

  // Identify the user in PostHog for future session stitching
  if (formData.email_address) {
    withPostHog((ph) => ph.identify(formData.email_address, {
      first_name: formData.first_name,
      last_name: formData.last_name,
      company: formData.company,
      ...utm,
    }));
  }

  // GTM dataLayer push for any tags configured in GTM (LinkedIn, Google Ads, etc.)
  if (typeof window !== "undefined" && (window as any).dataLayer) {
    (window as any).dataLayer.push({
      event: "contact_form_submitted",
      formData: {
        company: formData.company,
        has_email: !!formData.email_address,
      },
      ...utm,
    });
  }

  // LinkedIn Conversion tracking
  trackLinkedInConversion();
}

// ---------------------------------------------------------------------------
// LinkedIn Insight Tag
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    _linkedin_data_partner_ids?: string[];
    lintrk?: (action: string, data: Record<string, unknown>) => void;
  }
}

/** Initialize the LinkedIn Insight Tag. Call once on app load. */
export function initLinkedInInsightTag(partnerId: string): void {
  if (typeof window === "undefined" || !partnerId) return;

  // Set partner ID
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(partnerId);

  // Load the script
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  // noscript pixel fallback
  const noscript = document.createElement("noscript");
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.alt = "";
  img.src = `https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
}

/** Fire a LinkedIn conversion event (for form submissions, demo requests, etc.). */
export function trackLinkedInConversion(conversionId?: string): void {
  if (typeof window === "undefined" || !window.lintrk) return;
  if (conversionId) {
    window.lintrk("track", { conversion_id: conversionId });
  } else {
    // Default conversion — LinkedIn will attribute based on Insight Tag page views
    window.lintrk("track", { conversion_id: 0 });
  }
}

// ---------------------------------------------------------------------------
// Google Tag Manager
// ---------------------------------------------------------------------------

/**
 * Load GTM (container GTM-TWMGTLWN: the GA4 Google tag, and HubSpot until it
 * is removed there). The standard snippet, minus its inline-script form, so
 * gatsby-browser.js can start it on the visitor's first interaction instead
 * of from a <Script> in every layout.
 */
export function loadGtm(containerId: string): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ platform: "gatsby" });
  w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}
